import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, MapPinIcon, StarIcon } from 'lucide-react';
import { marketCategories, sellerFor } from '../../data/market';
import { naira } from '../../utils/format';
import { cn } from '../../utils/cn';
import type { MarketItem } from '../../types/market';

/**
 * The carousel card: image first, category named on it, save control top-right,
 * then title, place, price and rating. Saving is local to the person viewing —
 * it is never visible to the seller.
 */
export function RecentCard({
  item
}: {
  item: MarketItem;
}) {
  const [saved, setSaved] = useState(false);
  const seller = sellerFor(item.sellerId);
  const category = marketCategories.find(entry => entry.id === item.category);
  const place = item.area ?? seller?.displayName ?? category?.label;
  return <article className="group relative w-[15.5rem] shrink-0 overflow-hidden rounded-card border border-line bg-surface shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-[border-color,box-shadow] duration-150 ease-out hover:border-line-strong hover:shadow-[0_6px_18px_rgba(15,23,42,0.07)]" data-id="element-3058" data-mp="2n9r089eoysn">
      <div className="relative h-36 w-full overflow-hidden bg-canvas" data-id="element-3059" data-mp="1456jm7a679t7">
        {item.imageUrl ? <img src={item.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" data-id="element-3060" data-mp="q5x1v36bg7n4" /> : <span className="flex h-full w-full items-center justify-center text-h2 font-semibold text-ink-subtle/50" data-id="element-3061" data-mp="ggdhtu6va7qo">
            {item.title.charAt(0)}
          </span>}
        <span className="absolute bottom-2 left-2 rounded-md bg-surface/95 px-2 py-1 text-caption font-medium text-ink" data-id="element-3062" data-mp="7g7ayr5oc60h">
          {item.tags?.[0] ?? category?.label}
        </span>
        <button type="button" onClick={() => setSaved(current => !current)} aria-pressed={saved} aria-label={saved ? `Remove ${item.title} from saved` : `Save ${item.title}`} className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-surface/95 text-ink-muted transition-colors duration-150 ease-out hover:text-danger-700" data-id="element-3063" data-mp="cd7il6i69gtp">
          <HeartIcon aria-hidden className={cn('h-5 w-5', saved && 'fill-danger-700 text-danger-700')} data-id="element-3064" data-mp="35s3jt1mdjaek" />
        </button>
      </div>

      <div className="px-4 py-3.5" data-id="element-3065" data-mp="30utxhuua7euy">
        <Link to={`/market/item/${item.id}`} className="block truncate text-body font-semibold text-ink after:absolute after:inset-0 after:content-['']" data-id="element-3066" data-mp="2b3h6gb8rry7">
          {item.title}
        </Link>
        {place && <p className="mt-1 flex items-center gap-1 truncate text-caption text-ink-muted" data-id="element-3067" data-mp="3oha42t2tga2h">
            {item.area && <MapPinIcon aria-hidden className="h-3.5 w-3.5 shrink-0" data-id="element-3068" data-mp="3jiyv0kkci3lb" />}
            {place}
          </p>}
        <p className="cz-num mt-2 text-body font-semibold text-ink" data-id="element-3069" data-mp="2d4pqrelsqyzq">
          {item.priceNaira === undefined ? 'Quoted per job' : naira(item.priceNaira)}
          {item.priceNaira !== undefined && item.priceUnit && item.priceUnit !== 'each' && <span className="text-body-sm font-normal text-ink-muted" data-id="element-3070" data-mp="skm2wc990yso">
              {' '}
              / {item.priceUnit.replace('per ', '')}
            </span>}
        </p>
        {item.rating !== undefined && <p className="cz-num mt-1 flex items-center gap-1 text-caption text-ink-muted" data-id="element-3071" data-mp="12bys0w3nxav0">
            <StarIcon aria-hidden className="h-3.5 w-3.5 fill-warn-700 text-warn-700" data-id="element-3072" data-mp="179i881jx7xpl" />
            {item.rating.toFixed(1)}
            <span className="text-ink-subtle" data-id="element-3073" data-mp="1ba26x9k579du">({item.reviewCount})</span>
          </p>}
      </div>
    </article>;
}